"use client";

import { DealLabel, FavoriteButton, PriceTrackingButton, WishlistChart } from "@/components";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useDealType } from "@/hooks/useDealType";
import { useImpressionTracking } from "@/hooks/useImpressionTracking";
import { useMarketDifference } from "@/hooks/useMarketDifference";
import { useShareListing } from "@/hooks/useShareListing";
import { type Listing } from "@/services/api";
import { formatBrandName } from "@/utils/helpers/stringHelpers";
import Image from "next/image";

interface WishlistCardProps {
    listing: Listing;
    showDealLabel?: boolean;
}

const WishlistCard: React.FC<WishlistCardProps> = ({
    listing,
    showDealLabel = false
}) => {
    const { formatPrice } = useCurrency();
    const dealType = useDealType(listing);
    const marketDifference = useMarketDifference(listing);
    const handleShareClick = useShareListing(listing);

    const impressionRef = useImpressionTracking(listing, {
        threshold: 0.5,
        minVisibleTime: 1000,
        metadata: {
            source: "wishlist_card",
            show_deal_label: showDealLabel,
        }
    });

    return (
        <div className="relative" ref={impressionRef}>
            <a href={`/listings/${listing.ss_slug}`} className="block">
                <div className="aspect-square overflow-hidden relative">
                    <Image
                        src={listing.s3_image_urls[0] || "/placeholder-image.jpg"}
                        alt={`${listing.product.brand} ${listing.product.model}`}
                        fill
                        className="object-contain hover:scale-105 transition-transform p-6"
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
                    />
                    {showDealLabel && dealType !== 'none' && (
                        <DealLabel
                            type={dealType}
                            size="small"
                            className="absolute top-3 left-3"
                        />
                    )}
                    <div className="absolute top-2 right-2 flex flex-col sm:flex-row gap-2">
                        <FavoriteButton listing={listing} />
                        <PriceTrackingButton listing={listing} />
                    </div>
                </div>

                <div className="p-4">
                    <div className="mb-4">
                        <div className="font-bodoni uppercase text-base font-medium line-clamp-2">
                            {listing.product.model}
                        </div>
                        <div className="font-raleway text-sm text-black">
                            Pre-owned {formatBrandName(listing.product.brand)}
                        </div>
                    </div>

                    <div>
                        <div className="font-raleway text-sm font-medium mb-2">
                            {formatPrice(listing.current_price)}
                        </div>
                        {marketDifference && (
                            <div className="font-raleway text-xs text-gray-600">
                                Currently{' '}
                                <span className={marketDifference.isAbove ? 'text-[#FF2C2C]' : 'text-[#2E7D32]'}>
                                    {marketDifference.amount} {marketDifference.direction}
                                </span>
                                {' '}Sold Market Average
                            </div>
                        )}
                    </div>

                    {listing.vendor && (
                        <div className="font-raleway capitalize text-xs text-gray-500 mt-2">
                            {listing.vendor}
                        </div>
                    )}
                </div>
            </a>

            <div className="px-4 pb-4">
                <button
                    onClick={handleShareClick}
                    className="w-full flex items-center justify-center gap-2 py-3 border border-black bg-white text-black font-raleway text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                    <span>THROW IT ON THE GROUP CHAT</span>
                </button>
            </div>

            <div className="md:mx-0 md:w-auto max-md:relative max-md:left-1/2 max-md:right-1/2 max-md:-ml-[50vw] max-md:-mr-[50vw] max-md:w-screen max-md:max-w-none max-md:box-border">
                <div className="max-md:px-4">
                    <WishlistChart listing={listing} variant="full" />
                </div>
            </div>
        </div>
    );
};

export default WishlistCard;
